import React, { FC } from 'react';
import { Box, Typography, TextField, Button, IconButton, Link, Menu, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { formatDistanceToNow } from 'date-fns';

import { TrainingComment } from '../../../models/training-comment.models';
import { TrainingTeamAssignee } from '../../../models/training.models';
import { UserAvatar } from '../../user-avatar/user-avatar.component';
import { palette } from '../../../theme';

export function getEffortTierBackgroundColor(n: number): string {
  if (n <= 3) return palette.success.light;
  if (n <= 6) return palette.warning.light;
  if (n <= 8) return palette.error.main;
  return palette.error.dark;
}

export function getEffortTierTextColor(n: number): string {
  return n >= 7 ? '#ffffff' : palette.text.primary;
}

export interface CommentCardProps {
  variant: 'parent' | 'thread';
  comment: TrainingComment;
  headerName?: string;
  assignee?: TrainingTeamAssignee | null;
  isCoachReply?: boolean;
  showEffortBadge?: boolean;
  showMemberLink?: boolean;
  onMemberClick?: () => void;
  showReplyButton?: boolean;
  onReply?: () => void;
  canShowEditMenu?: boolean;
  commentMenuAnchorEl: HTMLElement | null;
  commentMenuId: number | null;
  onCommentMenuOpen: (event: React.MouseEvent<HTMLElement>, commentId: number) => void;
  onCommentMenuClose: () => void;
  onEditComment: (commentId: number) => void;
  onDeleteComment: (comment: TrainingComment) => void;
  editingCommentId: number | null;
  editingCommentMessage: string;
  onEditingMessageChange: (message: string) => void;
  onSaveComment: (commentId: number) => void;
  onCommentKeyDown: (e: React.KeyboardEvent) => void;
}

const CommentCard: FC<CommentCardProps> = ({
  variant,
  comment,
  headerName,
  assignee,
  isCoachReply = false,
  showEffortBadge = false,
  showMemberLink = false,
  onMemberClick,
  showReplyButton = false,
  onReply,
  canShowEditMenu = false,
  commentMenuAnchorEl,
  commentMenuId,
  onCommentMenuOpen,
  onCommentMenuClose,
  onEditComment,
  onDeleteComment,
  editingCommentId,
  editingCommentMessage,
  onEditingMessageChange,
  onSaveComment,
  onCommentKeyDown,
}) => {
  const isThread = variant === 'thread';
  const displayName = headerName ?? comment.creator;
  const relativeTime = comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }) : '';
  const isEdited = !!comment.updated_at && comment.updated_at !== comment.created_at;
  const fontSize = isThread ? 13 : 14;
  const avatarSize = isThread ? 28 : 36;

  const cardSx = isCoachReply
    ? {
        padding: 1.5,
        backgroundColor: '#F0F7FF',
        borderRadius: 2,
        border: `1px solid ${palette.grey[200]}`,
        borderLeft: `3px solid ${palette.primary.main}`,
      }
    : {
        padding: isThread ? 1.5 : 2,
        backgroundColor: palette.background.paper,
        borderRadius: isThread ? 2 : 3,
        border: `1px solid ${palette.grey[200]}`,
        boxShadow: isThread ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
      };

  return (
    <Box sx={{ ...(isThread ? { ml: 5 } : {}), mb: showReplyButton ? 1 : 0 }}>
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          {!isCoachReply && (
            <UserAvatar
              src={assignee?.profileImage}
              name={displayName}
              userId={assignee?.memberId}
              size={avatarSize}
              fontSize={isThread ? '0.65rem' : '0.75rem'}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              {isCoachReply ? (
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: palette.primary.main,
                  }}
                >
                  You replied
                </Typography>
              ) : showMemberLink && onMemberClick ? (
                <Link
                  component="button"
                  onClick={onMemberClick}
                  underline="hover"
                  sx={{
                    fontWeight: 600,
                    fontSize,
                    color: palette.primary.main,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {displayName}
                </Link>
              ) : (
                <Typography sx={{ fontWeight: 600, fontSize, color: palette.text.primary }}>{displayName}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {relativeTime}
                {isEdited ? ' (edited)' : ''}
              </Typography>
            </Box>
          </Box>
          {showEffortBadge && assignee?.effortRating != null && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: getEffortTierBackgroundColor(assignee.effortRating),
                color: getEffortTierTextColor(assignee.effortRating),
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {assignee.effortRating}/10
            </Box>
          )}
          {comment.can_edit && canShowEditMenu && (
            <>
              <IconButton
                size="small"
                onClick={(e) => onCommentMenuOpen(e, comment.id)}
                sx={{ padding: '2px', flexShrink: 0, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <MoreVertIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </IconButton>
              <Menu
                anchorEl={commentMenuAnchorEl}
                open={Boolean(commentMenuAnchorEl) && commentMenuId === comment.id}
                onClose={onCommentMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => onEditComment(comment.id)} sx={{ fontSize: 14 }}>
                  <EditIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  Edit
                </MenuItem>
                <MenuItem onClick={() => onDeleteComment(comment)} sx={{ fontSize: 14, color: 'error.main' }}>
                  <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
                  Delete
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {editingCommentId === comment.id ? (
          <TextField
            multiline
            fullWidth
            value={editingCommentMessage}
            onChange={(e) => onEditingMessageChange(e.target.value)}
            onBlur={() => onSaveComment(comment.id)}
            onKeyDown={onCommentKeyDown}
            autoFocus
            variant="outlined"
            sx={{
              fontSize,
              '& .MuiOutlinedInput-root': { fontSize, padding: '8px', borderRadius: 2 },
              '& .MuiOutlinedInput-input': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
            }}
            inputProps={{ style: { fontSize } }}
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize }}>
            {comment.message}
          </Typography>
        )}
      </Box>

      {showReplyButton && onReply && (
        <Button
          size="small"
          onClick={onReply}
          sx={{
            mt: 0.75,
            ml: isThread ? 5 : 0,
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 500,
            color: palette.primary.main,
            border: `1px solid ${palette.primary.light}`,
            borderRadius: 2,
            px: 1.5,
            py: 0.25,
            minWidth: 'auto',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
          }}
        >
          Reply
        </Button>
      )}
    </Box>
  );
};

export default CommentCard;
